import { Context, type Effect } from 'effect';

import type { CustomerProfile } from './customerAccountSchema';
import type { CustomerAccountError, CustomerAccountWriteError } from './errors';

/**
 * What a profile change asks for.
 *
 * Every field the settings page's contact card holds, and nothing else. It is
 * not the whole profile: the shop's own composed `fullName` is not editable, and
 * the default address has its own operation because it is a resource of its own.
 *
 * `email` is here because the card edits it. A shop that treats the address as
 * an identity it will not change refuses the write and says so, which is the
 * shop's rule to state rather than this port's to assume.
 *
 * **The newsletter consent is deliberately absent.** A caller can only send what
 * a form collected, and a form that shows no consent control collects `false` —
 * which would withdraw a consent the shopper gave, on a page that never asked.
 * A write cannot change what its page does not show, so the field is not here
 * and a provider echoes whatever the shop already holds. The first design with a
 * consent control adds it.
 */
export type UpdateCustomerProfile = {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneNumber: string;
};

/**
 * What a password change asks for: the one in use, and the new one twice.
 *
 * The confirmation travels because the backend takes it and validates it, and it
 * names `confirmNewPassword` back in a violation when the pair disagrees. A port
 * that dropped it would move a rule the shop owns into the browser.
 *
 * The current password is what makes this a change rather than a reset. A caller
 * who does not have it uses `CustomerSession.requestPasswordReset`.
 */
export type ChangeCustomerPassword = {
  readonly currentPassword: string;
  readonly newPassword: string;
  readonly confirmNewPassword: string;
};

/**
 * What saving the default address asks for.
 *
 * No `id`. Which address this replaces is the customer's current default, which
 * the provider already knows from the profile — so a caller cannot save over
 * somebody else's address by passing its id, and a customer with no default gets
 * one created.
 */
export type SaveCustomerAddress = {
  readonly firstName: string;
  readonly lastName: string;
  readonly street: string;
  readonly city: string;
  readonly postcode: string;
  readonly countryCode: string;
  readonly company: string;
  readonly businessId: string;
  readonly vatNumber: string;
  readonly phoneNumber: string;
};

/**
 * The signed-in customer's own record: read it, and change it.
 *
 * **The session is not a parameter.** Which customer this answers is decided by
 * the credential the client carries, not by anything a caller passes. That is
 * the other half of `.ai/plan/ENGINE_WRITE_TIER.md` section 3: a token that
 * ADDRESSES a resource is an operation parameter, and a credential that
 * AUTHENTICATES the caller is a layer. So `profile` takes nothing, and a
 * provider built over an anonymous client answers `Unauthorized`.
 *
 * `profile` is an `Effect` value rather than a function returning one, for the
 * reason `CatalogueMenu.roots` is: it takes no arguments, and a thunk over a
 * call with no arguments describes nothing more than the call.
 *
 * **Two of the three writes answer the refreshed profile, and the third
 * answers nothing.** That is the write tier's rule applied rather than bent:
 * `updateProfile` and `saveDefaultAddress` change what the page renders, and a
 * caller that had to re-read could not be told so by a `void`. A password change
 * alters nothing a page shows — the profile after it is the profile before it —
 * so there is no aggregate to restate.
 *
 * This tag is the session guard every account page runs behind. A page reads it
 * first and branches on `Unauthorized`, which is why that arm exists.
 *
 * A tenant provides it when its backend serves all four. One that shows a
 * profile but cannot change it does not provide it, and every settings page
 * fails to compile — which is the point of one tag per capability rather than
 * one service with optional methods.
 */
export type CustomerAccountService = {
  readonly profile: Effect.Effect<CustomerProfile, CustomerAccountError>;
  readonly updateProfile: (
    input: UpdateCustomerProfile
  ) => Effect.Effect<CustomerProfile, CustomerAccountWriteError>;
  readonly changePassword: (
    input: ChangeCustomerPassword
  ) => Effect.Effect<void, CustomerAccountWriteError>;
  readonly saveDefaultAddress: (
    input: SaveCustomerAddress
  ) => Effect.Effect<CustomerProfile, CustomerAccountWriteError>;
};

export class CustomerAccount extends Context.Service<CustomerAccount, CustomerAccountService>()(
  '@engine/Capability/CustomerAccount'
) {}
